import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse, PaginationInfo } from "./baseApi";
import { WorkOrder } from "./workOrderApi";
import { ProcessStep } from "./processFlowApi";

// Enum for WorkOrderOperationStatus
export enum WorkOrderOperationStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  PAUSED = 'PAUSED',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED'
}

// Main WorkOrderOperation interface
export interface WorkOrderOperation {
  id: number;
  operationNumber: number;
  operationName: string;
  operationDescription?: string;
  workCenter?: string;
  machineCode?: string;
  setupTimeHours?: number;
  runTimeHoursPerUnit?: number;
  totalEstimatedHours?: number;
  actualSetupHours?: number;
  actualRunHours?: number;
  totalActualHours?: number;
  quantityCompleted: number;
  quantityRejected: number;
  rejectionReason?: string;
  status: WorkOrderOperationStatus;
  startTime?: string;
  endTime?: string;
  productionDate?: string; // Date when production was completed
  assignedOperator?: string;
  qualityCheckStatus?: string;
  qualityCheckNotes?: string;
  isActive: boolean;
  createdBy?: number;
  updatedBy?: number;
  createdAt: string;
  updatedAt: string;
  
  // Relationships
  workOrder: WorkOrder;
  processStep?: ProcessStep;
}

// DTOs for API operations - following the same pattern as other APIs
export interface WorkOrderOperationCreateRequest {
  operationNumber?: number;
  operationName: string;
  operationDescription?: string;
  workCenter?: string;
  machineCode?: string;
  setupTimeHours?: number;
  runTimeHoursPerUnit?: number;
  totalEstimatedHours?: number;
  actualSetupHours?: number;
  actualRunHours?: number;
  totalActualHours?: number;
  quantityCompleted?: number;
  quantityRejected?: number;
  rejectionReason?: string;
  status?: WorkOrderOperationStatus;
  startTime?: string; // LocalDateTime as ISO string
  endTime?: string; // LocalDateTime as ISO string
  productionDate?: string; // Date when production was completed
  assignedOperator?: string;
  qualityCheckStatus?: string;
  qualityCheckNotes?: string;
  isActive?: boolean;
  workOrderId: number;
  processStepId?: number;
}

export interface WorkOrderOperationUpdateRequest extends WorkOrderOperationCreateRequest {
  id: number;
}

// DTO for bulk operation registration from WIP tracking
export interface WorkOrderOperationBulkCreateRequest {
  operationName: string; // Will be the next process name
  operationDescription?: string;
  workCenter?: string;
  machineCode: string;
  quantityCompleted: number;
  quantityRejected?: number;
  rejectionReason?: string;
  status?: WorkOrderOperationStatus;
  startTime: string; // LocalDateTime as ISO string
  endTime?: string; // LocalDateTime as ISO string
  productionDate?: string; // Date when production was completed
  assignedOperator: string;
  qualityCheckStatus?: string;
  qualityCheckNotes?: string;
  workOrderId: number;
  processStepId?: number;
  isActive?: boolean;
  createdBy?: number;
  updatedBy?: number;
  // Variant data as JSON object for multiple variants
  variantData?: {
    [variantId: number]: {
      productionQty: number;
      rejectQty: number;
    }
  };
}

export interface UpdateWorkOrderOperationDto {
  operationName?: string;
  operationDescription?: string;
  workCenter?: string;
  machineCode?: string;
  setupTimeHours?: number;
  runTimeHoursPerUnit?: number;
  totalEstimatedHours?: number;
  actualSetupHours?: number;
  actualRunHours?: number;
  totalActualHours?: number;
  quantityCompleted?: number;
  quantityRejected?: number;
  rejectionReason?: string;
  status?: WorkOrderOperationStatus;
  startTime?: string;
  endTime?: string;
  assignedOperator?: string;
  qualityCheckStatus?: string;
  qualityCheckNotes?: string;
  isActive?: boolean;
  processStepId?: number;
}

export interface WorkOrderOperationFilters {
  status?: WorkOrderOperationStatus;
  workCenter?: string;
  machineCode?: string;
  assignedOperator?: string;
  workOrderId?: number;
  processStepId?: number;
  isActive?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface WorkOrderOperationStats {
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
  cancelled: number;
  paused: number;
  qualityCheck: number;
  totalEstimatedHours: number;
  totalActualHours: number;
  totalQuantityCompleted: number;
  totalQuantityRejected: number;
}

class WorkOrderOperationApiService extends BaseApiService {

  // Get all work order operations with pagination and filters
  async getWorkOrderOperations(
    page: number = 1, 
    pageSize: number = 10,
    filters?: WorkOrderOperationFilters
  ): Promise<ApiResponse<WorkOrderOperation[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      // Add filters to query parameters
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      const response = await this.makeRequest<ApiResponse<WorkOrderOperation[]>>(
        `${API_ENDPOINTS.WORK_ORDER_OPERATIONS.LIST}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      // console.error('WorkOrderOperationApiService: Error fetching work order operations:', error);
      toast.error('Failed to fetch work order operations');
      throw error;
    }
  }

  // Get work order operation by ID
  async getWorkOrderOperationById(id: string): Promise<ApiResponse<WorkOrderOperation>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrderOperation>>(
        API_ENDPOINTS.WORK_ORDER_OPERATIONS.DETAIL(id)
      );
      return response;
    } catch (error) {
      toast.error('Failed to fetch work order operation details');
      throw error;
    }
  }

  // Create new work order operation
  async createWorkOrderOperation(operationData: WorkOrderOperationCreateRequest): Promise<ApiResponse<WorkOrderOperation>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrderOperation>>(
        API_ENDPOINTS.WORK_ORDER_OPERATIONS.CREATE, 
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(operationData),
        }
      );
      toast.success('Work order operation created successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to create work order operation');
      throw error;
    }
  }

  // Bulk register work order operations from WIP tracking
  async registerWorkOrderOperations(
    operationsData: WorkOrderOperationBulkCreateRequest[]
  ): Promise<ApiResponse<WorkOrderOperation[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrderOperation[]>>(
        API_ENDPOINTS.WORK_ORDER_OPERATIONS.BULK_REGISTER, 
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(operationsData),
        }
      );
      toast.success(`Successfully registered ${operationsData.length} work order operations!`);
      return response;
    } catch (error) {
      toast.error('Failed to register work order operations');
      throw error;
    }
  }

  // Update work order operation
  async updateWorkOrderOperation(id: string, operationData: WorkOrderOperationUpdateRequest): Promise<ApiResponse<WorkOrderOperation>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrderOperation>>(
        API_ENDPOINTS.WORK_ORDER_OPERATIONS.UPDATE(id), 
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(operationData),
        }
      );
      toast.success('Work order operation updated successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to update work order operation');
      throw error;
    }
  }

  // Delete work order operation
  async deleteWorkOrderOperation(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.WORK_ORDER_OPERATIONS.DELETE(id), 
        {
          method: HTTP_METHODS.DELETE,
        }
      );
      toast.success('Work order operation deleted successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to delete work order operation');
      throw error;
    }
  }

  // Search work order operations with pagination
  async searchWorkOrderOperations(
    query: string, 
    page: number = 1, 
    pageSize: number = 10,
    filters?: WorkOrderOperationFilters
  ): Promise<ApiResponse<WorkOrderOperation[]>> {
    try {
      const params = new URLSearchParams({
        q: query,
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      // Add filters to search parameters
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      const response = await this.makeRequest<ApiResponse<WorkOrderOperation[]>>(
        `${API_ENDPOINTS.WORK_ORDER_OPERATIONS.SEARCH}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to search work order operations');
      throw error;
    }
  }

  // Get work order operations by work order
  async getWorkOrderOperationsByWorkOrder(
    workOrderId: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkOrderOperation[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkOrderOperation[]>>(
        `${API_ENDPOINTS.WORK_ORDER_OPERATIONS.BY_WORK_ORDER(workOrderId)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch work order operations');
      throw error;
    }
  }

  // Get work order operations by status
  async getWorkOrderOperationsByStatus(
    status: WorkOrderOperationStatus, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkOrderOperation[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkOrderOperation[]>>(
        `${API_ENDPOINTS.WORK_ORDER_OPERATIONS.BY_STATUS(status)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error(`Failed to fetch ${status.toLowerCase()} work order operations`);
      throw error;
    }
  }

  // Get work order operations by work center
  async getWorkOrderOperationsByWorkCenter(
    workCenter: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkOrderOperation[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkOrderOperation[]>>(
        `${API_ENDPOINTS.WORK_ORDER_OPERATIONS.BY_WORK_CENTER(workCenter)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch work center operations');
      throw error;
    }
  }

  // Get work order operations by machine
  async getWorkOrderOperationsByMachine(
    machineCode: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkOrderOperation[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkOrderOperation[]>>(
        `${API_ENDPOINTS.WORK_ORDER_OPERATIONS.BY_MACHINE(machineCode)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch machine operations');
      throw error;
    }
  }

  // Get work order operations by operator
  async getWorkOrderOperationsByOperator(
    operator: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkOrderOperation[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkOrderOperation[]>>(
        `${API_ENDPOINTS.WORK_ORDER_OPERATIONS.BY_OPERATOR(operator)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch operator operations');
      throw error;
    }
  }

  // Get work order operations by process step
  async getWorkOrderOperationsByProcessStep(
    processStepId: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkOrderOperation[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkOrderOperation[]>>(
        `${API_ENDPOINTS.WORK_ORDER_OPERATIONS.BY_PROCESS_STEP(processStepId)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch process step operations');
      throw error;
    }
  }

  // Update work order operation status
  async updateWorkOrderOperationStatus(id: string, status: WorkOrderOperationStatus): Promise<ApiResponse<WorkOrderOperation>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrderOperation>>(
        API_ENDPOINTS.WORK_ORDER_OPERATIONS.UPDATE_STATUS(id), 
        {
          method: HTTP_METHODS.PATCH,
          body: JSON.stringify({ status }),
        }
      );
      toast.success(`Work order operation status updated to ${status.toLowerCase()}`);
      return response;
    } catch (error) {
      toast.error('Failed to update work order operation status');
      throw error;
    }
  }

  // Start operation
  async startOperation(id: string, startTime?: string): Promise<ApiResponse<WorkOrderOperation>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrderOperation>>(
        API_ENDPOINTS.WORK_ORDER_OPERATIONS.START_OPERATION(id), 
        {
          method: HTTP_METHODS.PATCH,
          body: JSON.stringify({ startTime: startTime || new Date().toISOString() }),
        }
      );
      toast.success('Operation started successfully');
      return response;
    } catch (error) {
      toast.error('Failed to start operation');
      throw error;
    }
  }

  // Complete operation
  async completeOperation(
    id: string, 
    endTime?: string, 
    actualSetupHours?: number, 
    actualRunHours?: number,
    quantityCompleted?: number,
    quantityRejected?: number,
    rejectionReason?: string
  ): Promise<ApiResponse<WorkOrderOperation>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrderOperation>>(
        API_ENDPOINTS.WORK_ORDER_OPERATIONS.COMPLETE_OPERATION(id), 
        {
          method: HTTP_METHODS.PATCH,
          body: JSON.stringify({ 
            endTime: endTime || new Date().toISOString(),
            actualSetupHours,
            actualRunHours,
            quantityCompleted,
            quantityRejected,
            rejectionReason
          }),
        }
      );
      toast.success('Operation completed successfully');
      return response;
    } catch (error) {
      toast.error('Failed to complete operation');
      throw error;
    }
  }

  // Pause operation
  async pauseOperation(id: string, notes?: string): Promise<ApiResponse<WorkOrderOperation>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrderOperation>>(
        API_ENDPOINTS.WORK_ORDER_OPERATIONS.PAUSE_OPERATION(id), 
        {
          method: HTTP_METHODS.PATCH,
          body: JSON.stringify({ notes }),
        }
      );
      toast.success('Operation paused successfully');
      return response;
    } catch (error) {
      toast.error('Failed to pause operation');
      throw error;
    }
  }

  // Resume operation
  async resumeOperation(id: string): Promise<ApiResponse<WorkOrderOperation>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrderOperation>>(
        API_ENDPOINTS.WORK_ORDER_OPERATIONS.RESUME_OPERATION(id), 
        {
          method: HTTP_METHODS.PATCH,
        }
      );
      toast.success('Operation resumed successfully');
      return response;
    } catch (error) {
      toast.error('Failed to resume operation');
      throw error;
    }
  }

  // Quality check
  async qualityCheck(
    id: string, 
    qualityCheckStatus: string, 
    qualityCheckNotes?: string
  ): Promise<ApiResponse<WorkOrderOperation>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrderOperation>>(
        API_ENDPOINTS.WORK_ORDER_OPERATIONS.QUALITY_CHECK(id), 
        {
          method: HTTP_METHODS.PATCH,
          body: JSON.stringify({ qualityCheckStatus, qualityCheckNotes }),
        }
      );
      toast.success('Quality check completed successfully');
      return response;
    } catch (error) {
      toast.error('Failed to complete quality check');
      throw error;
    }
  }

  // Get work order operation statistics
  async getWorkOrderOperationStats(filters?: WorkOrderOperationFilters): Promise<ApiResponse<WorkOrderOperationStats>> {
    try {
      let endpoint = API_ENDPOINTS.WORK_ORDER_OPERATIONS.GET_STATS;
      
      if (filters) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
        if (params.toString()) {
          endpoint += `?${params.toString()}`;
        }
      }

      const response = await this.makeRequest<ApiResponse<WorkOrderOperationStats>>(endpoint);
      return response;
    } catch (error) {
      toast.error('Failed to fetch work order operation statistics');
      throw error;
    }
  }

  // Export work order operations
  async exportWorkOrderOperations(filters?: WorkOrderOperationFilters, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    try {
      let endpoint = API_ENDPOINTS.WORK_ORDER_OPERATIONS.EXPORT;
      
      if (filters) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
        params.append('format', format);
        if (params.toString()) {
          endpoint += `?${params.toString()}`;
        }
      } else {
        endpoint += `?format=${format}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081/api/v1'}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      toast.error('Failed to export work order operations');
      throw error;
    }
  }

  // Helper method to extract data from API response
  extractData<T>(response: ApiResponse<T>): T {
    if (!response.success) {
      throw new Error(response.message || 'API request failed');
    }
    return response.data;
  }

  // Helper method to get work order operations with extracted data
  async getWorkOrderOperationsData(
    page: number = 1, 
    pageSize: number = 10,
    filters?: WorkOrderOperationFilters
  ): Promise<{ workOrderOperations: WorkOrderOperation[], pagination: PaginationInfo }> {
    const response = await this.getWorkOrderOperations(page, pageSize, filters);
    const workOrderOperations = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: workOrderOperations.length,
      pageSize: pageSize
    };
    
    return {
      workOrderOperations,
      pagination
    };
  }

  // Helper method to search work order operations with extracted data
  async searchWorkOrderOperationsData(
    query: string, 
    page: number = 1, 
    pageSize: number = 10,
    filters?: WorkOrderOperationFilters
  ): Promise<{ workOrderOperations: WorkOrderOperation[], pagination: PaginationInfo }> {
    const response = await this.searchWorkOrderOperations(query, page, pageSize, filters);
    const workOrderOperations = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: workOrderOperations.length,
      pageSize: pageSize
    };
    
    return {
      workOrderOperations,
      pagination
    };
  }

  // Helper method to get work order operations by work order with extracted data
  async getWorkOrderOperationsByWorkOrderData(
    workOrderId: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<{ workOrderOperations: WorkOrderOperation[], pagination: PaginationInfo }> {
    const response = await this.getWorkOrderOperationsByWorkOrder(workOrderId, page, pageSize);
    const workOrderOperations = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: workOrderOperations.length,
      pageSize: pageSize
    };
    
    return {
      workOrderOperations,
      pagination
    };
  }

  // Helper method to get work order operations by status with extracted data
  async getWorkOrderOperationsByStatusData(
    status: WorkOrderOperationStatus, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<{ workOrderOperations: WorkOrderOperation[], pagination: PaginationInfo }> {
    const response = await this.getWorkOrderOperationsByStatus(status, page, pageSize);
    const workOrderOperations = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: workOrderOperations.length,
      pageSize: pageSize
    };
    
    return {
      workOrderOperations,
      pagination
    };
  }
}

export const workOrderOperationApiService = new WorkOrderOperationApiService();
